import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Textarea } from '../components/ui/textarea';
import { 
  Users, UserPlus, MoreHorizontal, Trash2, Mail, Shield, 
  Building2, Clock, CheckCircle2, XCircle, AlertTriangle, ArrowLeft
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';

const TenantMembers = () => {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [inviteData, setInviteData] = useState({
    email: '',
    role: 'editor',
    message: ''
  });
  const [isInviting, setIsInviting] = useState(false);

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  const fetchMembers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/tenant-memberships/members');
      setMembers(response.data.members || []);
    } catch (error) {
      console.error('Failed to fetch members:', error);
      toast.error('Failed to load team members');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  const handleInvite = async () => {
    if (!inviteData.email) {
      toast.error('Please enter an email address');
      return;
    }

    setIsInviting(true);
    try {
      await api.post('/tenant-memberships/invite', inviteData);
      toast.success(`Invitation sent to ${inviteData.email}`);
      setShowInviteModal(false);
      setInviteData({ email: '', role: 'editor', message: '' });
      fetchMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invitation');
    } finally {
      setIsInviting(false);
    }
  };

  const handleRevoke = async () => {
    if (!selectedMember?.membership_id) return;

    try {
      await api.delete(`/tenant-memberships/${selectedMember.membership_id}`);
      toast.success('Access revoked successfully');
      setShowRevokeModal(false);
      setSelectedMember(null);
      fetchMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to revoke access');
    }
  };

  const getRoleBadge = (role) => {
    const colors = {
      owner: 'bg-purple-100 text-purple-700',
      admin: 'bg-red-100 text-red-700',
      super_admin: 'bg-red-100 text-red-700',
      editor: 'bg-blue-100 text-blue-700',
      viewer: 'bg-slate-100 text-slate-700',
      support: 'bg-amber-100 text-amber-700',
    };
    return colors[role] || colors.editor;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-100 text-green-700"><CheckCircle2 size={12} className="mr-1" /> Active</Badge>;
      case 'pending':
        return <Badge className="bg-amber-100 text-amber-700"><Clock size={12} className="mr-1" /> Pending</Badge>;
      case 'suspended':
        return <Badge className="bg-red-100 text-red-700"><XCircle size={12} className="mr-1" /> Suspended</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (!isAdmin) {
    return (
      <div className="p-8">
        <Alert>
          <AlertTriangle size={16} />
          <AlertDescription>
            You need admin privileges to manage team members.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-6xl mx-auto">
      {/* Back to Dashboard */}
      <Link 
        to="/dashboard" 
        className="inline-flex items-center gap-2 text-slate-500 hover:text-slate-700 mb-6 transition-colors"
      >
        <ArrowLeft size={16} />
        Back to Dashboard
      </Link>
      
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <Users size={32} />
            Organization Members
          </h1>
          <p className="text-slate-500 mt-1">
            Manage who has access to this organization
          </p>
        </div>
        <Button onClick={() => setShowInviteModal(true)} className="gap-2">
          <UserPlus size={18} />
          Invite User
        </Button>
      </div>

      {/* Info Banner */}
      <Alert className="mb-6 bg-indigo-50 border-indigo-200">
        <Building2 size={16} className="text-indigo-600" />
        <AlertDescription className="text-indigo-700">
          <strong>Multi-Organization Access:</strong> You can invite existing NexusCMS users to access your organization.
          They will be able to switch between their own organization and yours using the organization switcher.
        </AlertDescription>
      </Alert>

      <Card>
        <CardHeader>
          <CardTitle>Team Members</CardTitle>
          <CardDescription>
            {members.length} member{members.length !== 1 ? 's' : ''} with access to this organization
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-slate-500">Loading...</div>
          ) : members.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              No team members yet. Invite someone to get started!
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Last Active</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {members.map((member) => (
                  <TableRow key={member.user_id + (member.membership_id || '')}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{member.full_name || 'Unknown'}</p>
                        <p className="text-sm text-slate-500">{member.email}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={getRoleBadge(member.role)}>
                        {member.role}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {member.membership_type === 'primary' ? (
                        <Badge variant="outline" className="gap-1">
                          <Building2 size={12} />
                          Primary
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="gap-1 bg-slate-50">
                          <Users size={12} />
                          Guest
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {getStatusBadge(member.status)}
                    </TableCell>
                    <TableCell>
                      {member.last_accessed_at ? (
                        <span className="text-sm text-slate-500">
                          {new Date(member.last_accessed_at).toLocaleDateString()}
                        </span>
                      ) : member.accepted_at ? (
                        <span className="text-sm text-slate-500">
                          Joined {new Date(member.accepted_at).toLocaleDateString()}
                        </span>
                      ) : (
                        <span className="text-sm text-slate-400">Never</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {member.membership_type === 'guest' && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreHorizontal size={16} />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              className="text-red-600"
                              onClick={() => {
                                setSelectedMember(member);
                                setShowRevokeModal(true);
                              }}
                            >
                              <Trash2 size={14} className="mr-2" />
                              Revoke Access
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Invite Modal */}
      <Dialog open={showInviteModal} onOpenChange={setShowInviteModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus size={20} />
              Invite User to Organization
            </DialogTitle>
            <DialogDescription>
              Invite an existing NexusCMS user to access your organization. 
              They will receive an invitation they can accept or decline.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Email Address *</Label>
              <Input
                type="email"
                value={inviteData.email}
                onChange={(e) => setInviteData({ ...inviteData, email: e.target.value })}
                placeholder="user@example.com"
              />
              <p className="text-xs text-slate-500">
                The user must already have a NexusCMS account
              </p>
            </div>

            <div className="space-y-2">
              <Label>Access Role</Label>
              <Select 
                value={inviteData.role} 
                onValueChange={(v) => setInviteData({ ...inviteData, role: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">
                    <div className="flex items-center gap-2">
                      <Shield size={14} className="text-red-500" />
                      Admin - Full access
                    </div>
                  </SelectItem>
                  <SelectItem value="editor">
                    <div className="flex items-center gap-2">
                      <Users size={14} className="text-blue-500" />
                      Editor - Create & edit content
                    </div>
                  </SelectItem>
                  <SelectItem value="viewer">
                    <div className="flex items-center gap-2">
                      <Users size={14} className="text-slate-500" />
                      Viewer - Read-only access
                    </div>
                  </SelectItem>
                  <SelectItem value="support">
                    <div className="flex items-center gap-2">
                      <Users size={14} className="text-amber-500" />
                      Support - View & limited edit
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Personal Message (Optional)</Label>
              <Textarea
                value={inviteData.message}
                onChange={(e) => setInviteData({ ...inviteData, message: e.target.value })}
                placeholder="Add a personal note to the invitation..."
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInviteModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleInvite} disabled={isInviting} className="gap-2">
              <Mail size={16} />
              {isInviting ? 'Sending...' : 'Send Invitation'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revoke Access Modal */}
      <Dialog open={showRevokeModal} onOpenChange={setShowRevokeModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Revoke Access</DialogTitle>
            <DialogDescription>
              Are you sure you want to revoke access for this user?
            </DialogDescription>
          </DialogHeader>
          {selectedMember && (
            <Alert className="bg-red-50 border-red-200">
              <AlertDescription className="text-red-800">
                <strong>{selectedMember.full_name || selectedMember.email}</strong> will no longer be able to access this organization.
                They will still have access to their own organization.
              </AlertDescription>
            </Alert>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRevokeModal(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleRevoke}>
              Revoke Access
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TenantMembers;
